// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Container } from './container';
import { Tooltip } from './tooltip';
import { List } from './list';
import { emptyFn, render, createElement, createComponent, getComponent } from './common';

/**
 * @class Jsw.SmallTools
 * @extends Jsw.Container
 *
 * Example usage:
 *
 *     @example
 *     new Jsw.SmallTools({
 *         renderTo: document.body,
 *         operations: [{
 *             componentType: 'Jsw.SmallButton',
 *             title: 'Button 1',
 *             handler: function () {
 *                 alert('You clicked the Button 1!');
 *             }
 *         }, {
 *             componentType: 'Jsw.bar.Separator',
 *         }, {
 *             componentType: 'Jsw.SmallButton',
 *             title: 'Button 2',
 *             handler: function () {
 *                 alert('You clicked the Button 2!');
 *             }
 *         }]
 *     });
 */
export const SmallTools = Class.create(Container, {
    /**
     * @cfg {Object[]} operations
     */

    _initConfiguration($super, config) {
        $super(config);

        this._operations = this._getConfigParam('operations', null);
        this._operationsId = this._getConfigParam('operationsId', `${this._id}-operations`);
        this._cls = this._getConfigParam('cls', 'actions-box');

        if (Object.isArray(this._operations) && (0 === this._operations.size())) {
            this._operations = null;
        }

        this.searchFilters = $H(this._getConfigParam('searchFilters', {}));
        this.searchFiltersState = this._getConfigParam('searchFiltersState', {});
        this.searchHandler = this._getConfigParam('searchHandler', emptyFn);
        this.resetSearchHandler = this._getConfigParam('resetSearchHandler', emptyFn);
        this.listId = this._getConfigParam('listId');

        this.searchOveral = this._getConfigParam('searchOveral', null);
        if (null === this.searchOveral) {
            this.searchFilters.each(function (pair) {
                if (pair.value.overal) {
                    this.searchOverall = pair.key;
                }
            }, this);
            if (null === this.searchOveral && 1 === this.searchFilters.size()) {
                this.searchOveral = this.searchFilters.keys().first();
            }
            if (null === this.searchOveral) {
                this.searchOveral = false;
            }
        }

        this.quickSearchWide = this._getConfigParam('quickSearchWide', false);
        this.quickSearchInputId = this._getConfigParam('quickSearchInputId');
        this.searchCollapsed = this._getConfigParam('searchCollapsed', false);
        this.onToggleSearch = this._getConfigParam('onToggleSearch', emptyFn);
    },

    render($super) {
        $super();

        render(this._componentElement, [
            createElement(`#${this._operationsId}.objects-toolbar.clearfix`,
                this._operations ? this._operations.map(createComponent) : '',
                this.quickSearchBoxView()
            ),
            this.searchBoxView(),
        ]);

        this._componentElement.toggleClassName('actions-box-search-show', !this.searchCollapsed);

        if (this._operations) {
            this.addResponsiveButton('toolbar');
        }

        if (this.searchFilters.size()) {
            this.addResponsiveButton('search');
        }

        Tooltip.initData(this._componentElement);
    },

    quickSearchBoxView() {
        if (!this.searchFilters.size()) {
            return '';
        }

        return createElement(`.quick-search-box${this.quickSearchWide ? '.quick-search-box-wide' : ''}`,
            {
                onclick(event) {
                    event.stopPropagation();
                },
            },
            this.searchOveralView(),
            this.searchFilters.size() > 1 ? createElement('span.search-control',
                createElement('a.s-btn.sb-search-show', { onclick: this.toggleSearch.bind(this) }, this.lmsg('showSearch')),
                createElement('a.s-btn.sb-search-hide', { onclick: this.toggleSearch.bind(this) }, this.lmsg('hideSearch'))
            ) : ''
        );
    },

    searchOveralView() {
        if (!this.searchOveral) {
            return '';
        }

        const filterState = this.searchFiltersState[this.searchOveral];
        const currentValue = this.searchFilters.get(this.searchOveral).value || (filterState && filterState.searchText) || '';

        return createElement('.search-field',
            createElement('form',
                {
                    onsubmit: function (event) {
                        Event.stop(event);
                        this.searchHandler(event.element());
                    }.bind(this),
                },
                createElement(`input${this.quickSearchInputId ? `#${this.quickSearchInputId}` : ''}`, {
                    type: 'text',
                    value: currentValue,
                    name: `searchFilter[${this.searchOveral}][searchText]`,
                }),
                createElement('em',
                    {
                        onclick: function (event) {
                            this.searchHandler(event.element().up('form'));
                        }.bind(this),
                    },
                    createElement('span')
                )
            )
        );
    },

    searchBoxView() {
        if (this.searchFilters.size() < 2) {
            return;
        }

        return createElement('.search-box',
            {
                onclick(event) {
                    event.stopPropagation();
                },
            },
            createElement('form',
                {
                    onsubmit: function (event) {
                        this.searchHandler(event.element());
                        event.stop();
                    }.bind(this),
                },
                createElement('ul',
                    this.searchFiltersView()
                ),
                createElement('a.s-btn.sb-search',
                    {
                        onclick: function (event) {
                            this.searchHandler(event.element().up('form'));
                        }.bind(this),
                    },
                    this.lmsg('buttonSearch')
                ),
                createElement('a.s-btn.sb-show-all',
                    {
                        onclick: function () {
                            this.resetSearchHandler();
                        }.bind(this),
                    },
                    this.lmsg('buttonResetSearch')
                ),
                createElement(`input[type="image"][style="border: 0pt none ; height: 0pt; width: 0pt; position: absolute; left: -9999px;"][src="${Jsw.skinUrl}/images/blank.gif"]`)
            )
        );
    },

    searchFiltersView() {
        const filters = [];

        this.searchFilters.each(function (pair) {
            const name = pair.key;
            const config = pair.value;

            filters.push(
                createElement('li',
                    createElement('div',
                        config.title ? createElement('span', config.title) : '',
                        List.Filters.create(Object.extend({
                            idPrefix: this.listId,
                            name,
                            value: this.searchFiltersState[name] && this.searchFiltersState[name].searchText,
                        }, config), function (event) {
                            this.searchHandler(event.element().up('form'));
                        }.bind(this))
                    )
                )
            );
        }, this);

        return filters;
    },

    toggleSearch() {
        this._componentElement.toggleClassName('actions-box-search-show');
        this.onToggleSearch(!this._componentElement.hasClassName('actions-box-search-show'));
    },

    _addEvents($super) {
        $super();

        $(document).observe('click', function () {
            this._hideResponsiveButtonBlocks();
        }.bind(this));
    },

    addResponsiveButton(name) {
        if (!this._rActionsElement) {
            this._rActionsElement = new Element('div', { class: 'r-actions' }).update('<ul class="r-actions-list"></ul>');
            this._componentElement.insert({ top: this._rActionsElement });
        }

        if (this._rActionsElement.down(`.r-actions-${name}`)) {
            return;
        }

        this._rActionsElement.down('.r-actions-list').insert(
            `<li class="r-actions-item r-actions-${name}" onclick="Jsw.getComponent('${this._id}').onResponsiveButtonClick(event, '${name}');"><span></span></li>`
        );
    },

    onResponsiveButtonClick(event, name) {
        const button = this._componentElement.down(`.r-actions-${name}`);
        const show = !button.hasClassName('r-actions-item-active');
        this._hideResponsiveButtonBlocks();
        if (show) {
            this._showResponsiveButtonBlocks(name);
        }
        event.stopPropagation();
    },

    _hideResponsiveButtonBlocks() {
        $w(this._componentElement.className).each(function (className) {
            if (className.indexOf('r-actions-active') === 0) {
                this._componentElement.removeClassName(className);
            }
        }.bind(this));
        this._componentElement.select('.r-actions-item-active').each(function (item) {
            if (item.hasClassName('r-actions-select')) {
                return;
            }
            item.removeClassName('r-actions-item-active');
        });
    },

    _showResponsiveButtonBlocks(name) {
        this._componentElement.down(`.r-actions-${name}`).addClassName('r-actions-item-active');
        this._componentElement.addClassName('r-actions-active');
        this._componentElement.addClassName(`r-actions-active-${name}`);
    },
});



// WEBPACK FOOTER //
// ./jsw/small-tools.js